import { CalendarDate, getLocalTimeZone, today } from '@internationalized/date';
import { Maybe, rd } from '@passionware/monads';
import { TFunction } from 'i18next';
import { Calendar } from 'lucide-react';
import {
  createDateFilter,
  dateFilterSchemaOf,
  GenericDateFilter
} from 'v5/api/_common/query/filters/GenericDateFilter';
import { FilterDateConfig } from 'v5/patterns/filters/FilterWidget.types';
import { convertDateFilterToApi } from 'v5/platform/filters/filterConverters';

const timestampFilterPresetIds = [
  'last7Days',
  'last30Days',
  'last90Days'
] as const;

export type TimestampFilterPreset = (typeof timestampFilterPresetIds)[number];

export type TimestampFilter = GenericDateFilter<TimestampFilterPreset>;

function resolveRange(
  preset: TimestampFilterPreset,
  today: CalendarDate
): {
  from: CalendarDate;
  to: CalendarDate;
} {
  switch (preset) {
    case 'last7Days':
      return {
        from: today.subtract({ days: 6 }),
        to: today
      };
    case 'last30Days':
      return {
        from: today.subtract({ days: 29 }),
        to: today
      };
    case 'last90Days':
      return {
        from: today.subtract({ days: 89 }),
        to: today
      };
  }
}

const createRangeMatcher =
  (preset: TimestampFilterPreset) =>
  (date: CalendarDate, now: CalendarDate) => {
    const { from, to } = resolveRange(preset, now);
    return date.compare(from) >= 0 && date.compare(to) <= 0;
  };

function createTimestampFilter(
  label: string,
  t: TFunction<'core'>
): FilterDateConfig<TimestampFilterPreset> {
  return {
    label,
    icon: <Calendar />,
    type: 'date',
    createDefaultValue: () => ({ operator: 'preset', value: 'last90Days' }),
    options: {
      useOptions() {
        return rd.of(
          timestampFilterPresetIds.map(preset => ({
            value: preset,
            label: t(`standard-date-filter.timestamp.${preset}`)
          }))
        );
      },
      useResolver() {
        return rd.of(
          createDateFilter(
            Object.fromEntries(
              timestampFilterPresetIds.map(preset => [
                preset,
                {
                  match: createRangeMatcher(preset)
                }
              ])
            )
          )
        );
      }
    }
  };
}

function createTimestampHttpConverter() {
  return (filter: Maybe<TimestampFilter>) => {
    return filter
      ? convertDateFilterToApi<TimestampFilterPreset>(filter, {
          resolvePreset: preset =>
            resolveRange(preset, today(getLocalTimeZone()))
        })
      : null;
  };
}

const timestampFilterSchema = dateFilterSchemaOf([
  'last7Days',
  'last30Days',
  'last90Days'
]);

export const timestampFilter = {
  createFilterConfig: createTimestampFilter,
  createHttpConverter: createTimestampHttpConverter,
  resolveRange,
  schema: timestampFilterSchema
};
